import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import ProductComment from "../../../../components/listing/comment/ProductComment";
import ProductGrid from "../../../../components/listing/product-card/ProductGrid";
import {
  getProductsBySellerId,
  getSimilarProducts,
} from "../../../../service/listing/detailListingService";
import AuthGuard from "../../../../components/shared/AuthGuard";

const ProductDetailPage = () => {
  const { productID } = useParams();
  const [product, setProduct] = useState(null);
  const [userListings, setUserListings] = useState([]);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("description");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setLoading(true);

        // Fetch product detail
        const productResponse = await fetch(
          `https://iudi-api.thinkdiff.us/api/listings/${productID}`
        );
        const productData = await productResponse.json();

        if (!productResponse.ok) {
          throw new Error("Không tìm thấy sản phẩm");
        }

        setProduct(productData);

        // Fetch user listings if user ID exists
        if (productData.id_user_post) {
          try {
            const sellerProducts = await getProductsBySellerId(
              productData.id_user_post
            );
            // Filter out current product
            const otherSellerProducts = sellerProducts.filter(
              (item) =>
                String(item.ListingID) !== String(productID) &&
                String(item.ID) !== String(productID)
            );
            setUserListings(otherSellerProducts);
          } catch (err) {
            console.warn(
              "Không thể tải danh sách sản phẩm của người bán:",
              err
            );
          }
        }

        // Fetch similar products by category
        if (productData.CategoryID) {
          try {
            const similarItems = await getSimilarProducts(
              productData.CategoryID
            );
            // Filter out current product
            const otherSimilarProducts = similarItems.filter(
              (item) =>
                String(item.ListingID) !== String(productID) &&
                String(item.ID) !== String(productID)
            );
            setSimilarProducts(otherSimilarProducts);
          } catch (err) {
            console.warn("Không thể tải danh sách sản phẩm tương tự:", err);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error(err);
        setError(err.message || "Không thể tải dữ liệu sản phẩm");
        setLoading(false);
      }
    };

    if (productID) {
      fetchProductData();
    }
  }, [productID]);

  const formatPrice = (price) => {
    if (!price) return "Liên hệ";
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("vi-VN");
  };

  const handleImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  if (loading) {
    return (
      <div className="product-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Đang tải thông tin sản phẩm...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-detail-page">
        <div className="error-container">
          <h2>Lỗi</h2>
          <p>{error}</p>
          <Link to="/market/thanh-ly" className="back-btn">
            Quay lại
          </Link>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-detail-page">
        <div className="error-container">
          <h2>Không tìm thấy sản phẩm</h2>
          <p>Sản phẩm này có thể đã bị xóa hoặc không tồn tại.</p>
          <Link to="/market/thanh-ly" className="back-btn">
            Quay lại
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/market" className="breadcrumb-link">
          Trang chủ
        </Link>
        <span className="breadcrumb-separator">/</span>
        <Link to="/market/thanh-ly" className="breadcrumb-link">
          Thanh lý
        </Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">{product.title}</span>
      </div>

      <div className="product-content">
        {/* Main Product Info */}
        <div className="product-main">
          <div className="product-gallery">
            <div className="main-image">
              {product.danh_sach_anh &&
              product.danh_sach_anh[selectedImageIndex] ? (
                <img
                  src={product.danh_sach_anh[selectedImageIndex]}
                  alt={product.title}
                />
              ) : (
                <div className="placeholder-image">
                  <span>📷</span>
                  <p>Không có hình ảnh</p>
                </div>
              )}
            </div>
            {/* Hiển thị các ảnh nhỏ */}
            {product.danh_sach_anh && product.danh_sach_anh.length > 1 && (
              <div className="gallery-thumbnails-wrapper">
                <div className="gallery-thumbnails">
                  {product.danh_sach_anh.map((img, idx) => (
                    <div
                      className={`thumbnail-image ${
                        selectedImageIndex === idx ? "active" : ""
                      }`}
                      key={idx}
                      onClick={() => handleImageClick(idx)}
                    >
                      <img src={img} alt={`Ảnh sản phẩm ${idx + 1}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="product-info">
            <div className="product-header">
              <h1 className="product-title">{product.Title}</h1>
              <div className="product-meta">
                <span className="product-category">{product.CategoryName}</span>
                <span className="product-date">
                  {formatDate(product.created_time)}
                </span>
              </div>
            </div>

            <div className="product-price-section">
              <div className="price-main">{formatPrice(product.price)}</div>
              <div className="price-details">
                <span className="currency">{product.currency}</span>
                <span className="location">📍 {product.Address}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="product-actions">
              <button className="btn-primary">
                <span>📞</span>
                Liên hệ người bán
              </button>
              <button className="btn-secondary">
                <span>💬</span>
                Chat ngay
              </button>
            </div>

            {/* Seller Info */}
            <div className="seller-info">
              <h3>Thông tin người bán</h3>
              <div className="seller-details">
                <div className="seller-avatar">
                  <span>👤</span>
                </div>
                <div className="seller-text">
                  <p className="seller-name">
                    {product.SellerName || "Chưa cập nhật"}
                  </p>
                  <p className="seller-location">{product.Address}</p>
                  <p className="seller-platform">
                    Đăng trên: {product.platform}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details Tabs */}
        <div className="product-details">
          <div className="tabs-header">
            <button
              className={`tab-btn ${
                activeTab === "description" ? "active" : ""
              }`}
              onClick={() => setActiveTab("description")}
            >
              Mô tả chi tiết
            </button>
            <button
              className={`tab-btn ${
                activeTab === "specifications" ? "active" : ""
              }`}
              onClick={() => setActiveTab("specifications")}
            >
              Thông số kỹ thuật
            </button>
            <button
              className={`tab-btn ${activeTab === "comments" ? "active" : ""}`}
              onClick={() => setActiveTab("comments")}
            >
              Bình luận
            </button>
            <button
              className={`tab-btn ${
                activeTab === "seller-products" ? "active" : ""
              }`}
              onClick={() => setActiveTab("seller-products")}
            >
              Sản phẩm khác của người bán ({userListings.length})
            </button>
          </div>

          <div className="tabs-content">
            {activeTab === "description" && (
              <div className="tab-panel">
                <div className="description-content">
                  {product.Description ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: product.Description.replace(/\n/g, "<br/>"),
                      }}
                    />
                  ) : (
                    <p>Chưa có mô tả chi tiết</p>
                  )}
                </div>

                {/* Hashtags */}
                {product.Hashtags && product.Hashtags.length > 0 && (
                  <div className="hashtags-section">
                    <h4>Tags</h4>
                    <div className="hashtags">
                      {product.Hashtags.map((tag, index) => (
                        <span key={index} className="hashtag">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "specifications" && (
              <div className="tab-panel">
                <div className="specifications">
                  <div className="spec-item">
                    <span className="spec-label">Mã sản phẩm:</span>
                    <span className="spec-value">{product.ListingID}</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Danh mục:</span>
                    <span className="spec-value">{product.CategoryName}</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Địa chỉ:</span>
                    <span className="spec-value">{product.Address}</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Quận/Huyện:</span>
                    <span className="spec-value">{product.DistrictName}</span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Ngày đăng:</span>
                    <span className="spec-value">
                      {formatDate(product.created_time)}
                    </span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Cập nhật lần cuối:</span>
                    <span className="spec-value">
                      {formatDate(product.updated_time)}
                    </span>
                  </div>
                  <div className="spec-item">
                    <span className="spec-label">Trạng thái:</span>
                    <span className="spec-value">
                      {product.status || "Đang bán"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "comments" && (
              <div className="tab-panel">
                <AuthGuard
                  feature="Bình luận"
                  message="Bạn cần đăng nhập để xem và viết bình luận"
                  showLockIcon={true}
                  blurContent={true}
                >
                  <ProductComment listingId={productID} />
                </AuthGuard>
              </div>
            )}

            {activeTab === "seller-products" && (
              <div className="tab-panel">
                <div className="seller-products">
                  {userListings.length > 0 ? (
                    <ProductGrid listings={userListings.slice(0, 6)} />
                  ) : (
                    <div className="no-products">
                      <p>Người bán này chưa có sản phẩm khác</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tin rao khác của người bán - Horizontal Slider */}
        {userListings.length > 0 && (
          <div className="other-listings-section">
            <h2 className="section-title">
              Tin rao khác của {product.SellerName || "người bán"}
            </h2>
            <div className="horizontal-slider">
              <button
                className="slider-btn slider-btn-prev"
                onClick={() => {
                  const container = document.querySelector(".slider-container");
                  container.scrollBy({ left: -300, behavior: "smooth" });
                }}
              >
                ‹
              </button>
              <div className="slider-container">
                <ProductGrid listings={userListings.slice(0, 12)} />
              </div>
              <button
                className="slider-btn slider-btn-next"
                onClick={() => {
                  const container = document.querySelector(".slider-container");
                  container.scrollBy({ left: 300, behavior: "smooth" });
                }}
              >
                ›
              </button>
            </div>
          </div>
        )}

        {/* Tin đăng tương tự - Standard Grid */}
        {similarProducts.length > 0 && (
          <div className="similar-products-section">
            <h2 className="section-title">Tin đăng tương tự</h2>
            <div className="similar-products-grid">
              <ProductGrid listings={similarProducts.slice(0, 12)} />
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .product-detail-page {
          min-height: calc(100vh - 60px);
          background-color: #0f172a;
          color: #e5e7eb;
          padding: 20px;
        }

        .breadcrumb {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 24px;
          font-size: 14px;
        }

        .breadcrumb-link {
          color: #4ade80;
          text-decoration: none;
          transition: color 0.2s;
        }

        .breadcrumb-link:hover {
          color: #22c55e;
        }

        .breadcrumb-separator {
          color: #64748b;
        }

        .breadcrumb-current {
          color: #cbd5e1;
        }

        .product-content {
          max-width: 1200px;
          margin: 0 auto;
        }

        .product-main {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 32px;
          margin-bottom: 32px;
        }

        .product-gallery {
          background: #1e293b;
          border-radius: 16px;
          padding: 24px;
          border: 1px solid #334155;
        }

        .main-image {
          width: 100%;
          height: 400px;
          background: #334155;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .main-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .placeholder-image {
          text-align: center;
          color: #94a3b8;
        }

        .placeholder-image span {
          font-size: 4rem;
          display: block;
          margin-bottom: 16px;
        }

        .gallery-thumbnails-wrapper {
          margin-top: 16px;
          overflow: hidden;
        }

        .gallery-thumbnails {
          display: flex;
          gap: 12px;
          overflow-x: auto;
          overflow-y: hidden;
          scroll-behavior: smooth;
          padding-bottom: 8px;
          scrollbar-width: thin;
          scrollbar-color: #4ade80 #334155;
        }

        .gallery-thumbnails::-webkit-scrollbar {
          height: 6px;
        }

        .gallery-thumbnails::-webkit-scrollbar-track {
          background: #334155;
          border-radius: 3px;
        }

        .gallery-thumbnails::-webkit-scrollbar-thumb {
          background: #4ade80;
          border-radius: 3px;
        }

        .gallery-thumbnails::-webkit-scrollbar-thumb:hover {
          background: #22c55e;
        }

        .thumbnail-image {
          flex: 0 0 calc(20% - 9.6px); /* Hiển thị 5 ảnh (20% mỗi ảnh, trừ gap) */

          height: 80px;
          background: #334155;
          border-radius: 8px;
          overflow: hidden;
          cursor: pointer;
          border: 2px solid transparent;
          transition: all 0.3s ease;
        }

        .thumbnail-image:hover {
          border-color: #4ade80;
          transform: scale(1.05);
        }

        .thumbnail-image.active {
          border-color: #4ade80;
          box-shadow: 0 0 0 2px rgba(74, 222, 128, 0.2);
        }

        .thumbnail-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .product-info {
          background: #1e293b;
          border-radius: 16px;
          padding: 24px;
          border: 1px solid #334155;
        }

        .product-header {
          margin-bottom: 24px;
        }

        .product-title {
          font-size: 1.875rem;
          font-weight: 700;
          color: #f1f5f9;
          margin-bottom: 12px;
          line-height: 1.3;
        }

        .product-meta {
          display: flex;
          gap: 16px;
          font-size: 14px;
        }

        .product-category {
          color: #4ade80;
          background: rgba(74, 222, 128, 0.1);
          padding: 4px 8px;
          border-radius: 4px;
        }

        .product-date {
          color: #94a3b8;
        }

        .product-price-section {
          margin-bottom: 24px;
          padding: 20px;
          background: #334155;
          border-radius: 12px;
          border: 1px solid #475569;
        }

        .price-main {
          font-size: 2rem;
          font-weight: 700;
          color: #f1f5f9;
          margin-bottom: 8px;
        }

        .price-details {
          display: flex;
          gap: 16px;
          font-size: 14px;
          color: #cbd5e1;
        }

        .currency {
          color: #4ade80;
        }

        .location {
          color: #94a3b8;
        }

        .product-actions {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
        }

        .btn-primary,
        .btn-secondary {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px 16px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }

        .btn-primary {
          background: #4ade80;
          color: #0f172a;
        }

        .btn-primary:hover {
          background: #22c55e;
        }

        .btn-secondary {
          background: transparent;
          color: #4ade80;
          border: 1px solid #4ade80;
        }

        .btn-secondary:hover {
          background: #4ade80;
          color: #0f172a;
        }

        .seller-info h3 {
          font-size: 1.125rem;
          font-weight: 600;
          color: #f1f5f9;
          margin-bottom: 16px;
        }

        .seller-details {
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .seller-avatar {
          width: 48px;
          height: 48px;
          background: #334155;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }

        .seller-text p {
          margin: 0 0 4px 0;
          font-size: 14px;
        }

        .seller-name {
          color: #f1f5f9;
          font-weight: 600;
        }

        .seller-location {
          color: #94a3b8;
        }

        .seller-platform {
          color: #4ade80;
        }

        .product-details {
          background: #1e293b;
          border-radius: 16px;
          border: 1px solid #334155;
          overflow: hidden;
        }

        .tabs-header {
          display: flex;
          border-bottom: 1px solid #334155;
        }

        .tab-btn {
          background: transparent;
          border: none;
          color: #cbd5e1;
          padding: 16px 24px;
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
          border-bottom: 2px solid transparent;
        }

        .tab-btn:hover {
          color: #f1f5f9;
        }

        .tab-btn.active {
          color: #4ade80;
          border-bottom-color: #4ade80;
        }

        .tabs-content {
          padding: 24px;
        }

        .description-content {
          line-height: 1.6;
          color: #cbd5e1;
        }

        .hashtags-section {
          margin-top: 24px;
        }

        .hashtags-section h4 {
          color: #f1f5f9;
          margin-bottom: 12px;
        }

        .hashtags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .hashtag {
          background: #334155;
          color: #4ade80;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
        }

        .specifications {
          display: grid;
          gap: 12px;
        }

        .spec-item {
          display: flex;
          justify-content: space-between;
          padding: 12px 0;
          border-bottom: 1px solid #334155;
        }

        .spec-label {
          color: #cbd5e1;
          font-weight: 500;
        }

        .spec-value {
          color: #f1f5f9;
        }

        .seller-products {
          margin-top: 16px;
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 16px;
        }

        .product-card {
          background: #334155;
          border-radius: 12px;
          overflow: hidden;
          text-decoration: none;
          color: inherit;
          border: 1px solid #475569;
          transition: all 0.3s ease;
        }

        .product-card:hover {
          transform: translateY(-2px);
          border-color: #4ade80;
        }

        .product-card .product-image {
          height: 150px;
          background: #475569;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .product-card .product-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .product-card .product-info {
          padding: 12px;
        }

        .product-card .product-title {
          font-size: 14px;
          margin-bottom: 4px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .product-card .product-category {
          font-size: 12px;
          color: #4ade80;
          margin-bottom: 4px;
        }

        .product-card .product-price {
          font-size: 14px;
          font-weight: 600;
          color: #f1f5f9;
          margin-bottom: 4px;
        }

        .product-card .product-location {
          font-size: 12px;
          color: #94a3b8;
        }

        .no-products {
          text-align: center;
          padding: 40px;
          color: #cbd5e1;
        }

        .loading-container,
        .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          text-align: center;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #334155;
          border-top: 4px solid #4ade80;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .error-container h2 {
          color: #f1f5f9;
          margin-bottom: 16px;
        }

        .back-btn {
          background: #4ade80;
          color: #0f172a;
          padding: 12px 24px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          margin-top: 16px;
        }

        /* Other Listings Section - Horizontal Slider */
        .other-listings-section {
          background: #1e293b;
          border-radius: 16px;
          padding: 32px 24px;
          margin-top: 32px;
          border: 1px solid #334155;
        }

        .section-title {
          font-size: 1.75rem;
          font-weight: 700;
          color: #f1f5f9;
          margin-bottom: 24px;
          font-family: "Lexend", sans-serif;
        }

        .horizontal-slider {
          position: relative;
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .slider-container {
          overflow-x: auto;
          scroll-behavior: smooth;
          scrollbar-width: none; /* Firefox */
          -ms-overflow-style: none; /* IE and Edge */
        }

        .slider-container::-webkit-scrollbar {
          display: none; /* Chrome, Safari, Opera */
        }

        .slider-container .product-grid-layout {
          display: flex;
          flex-wrap: nowrap;
          gap: 16px;
        }

        .slider-container .product-grid-layout > * {
          flex: 0 0 250px;
          min-width: 250px;
        }

        .slider-btn {
          position: absolute;
          background: rgba(74, 222, 128, 0.9);
          color: #0f172a;
          border: none;
          border-radius: 50%;
          width: 48px;
          height: 48px;
          font-size: 2rem;
          font-weight: 700;
          cursor: pointer;
          z-index: 10;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }

        .slider-btn:hover {
          background: #4ade80;
          transform: scale(1.1);
        }

        .slider-btn-prev {
          left: 0;
        }

        .slider-btn-next {
          right: 0;
        }

        /* Similar Products Section - Standard Grid */
        .similar-products-section {
          background: #1e293b;
          border-radius: 16px;
          padding: 32px 24px;
          margin-top: 32px;
          border: 1px solid #334155;
        }

        .similar-products-grid .product-grid-layout {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 16px;
        }

        @media (max-width: 768px) {
          .product-main {
            grid-template-columns: 1fr;
            gap: 20px;
          }

          .product-title {
            font-size: 1.5rem;
          }

          .price-main {
            font-size: 1.5rem;
          }

          .product-actions {
            flex-direction: column;
          }

          .tabs-header {
            flex-direction: column;
          }

          .tab-btn {
            text-align: left;
            border-bottom: 1px solid #334155;
            border-right: none;
          }

          .tab-btn.active {
            border-bottom-color: #334155;
            border-left: 2px solid #4ade80;
          }

          .products-grid {
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          }

          .section-title {
            font-size: 1.25rem;
          }

          .slider-btn {
            width: 40px;
            height: 40px;
            font-size: 1.5rem;
          }

          .slider-container .product-grid-layout > * {
            flex: 0 0 200px;
            min-width: 200px;
          }

          .similar-products-grid .product-grid-layout {
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          }
        }
      `}</style>
    </div>
  );
};

export default ProductDetailPage;
